import { useMemo, useState } from 'react';
import { CheckCircle2, Code2, Crosshair, ExternalLink, Plus, XCircle } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { ReportAssetsProvider } from '@/frontend/reports/primitives';
import { InputSheet, InputValues, emptyInputs, normalizeInputs, sheetProgress } from '@/reports/inputs';
import { moduleFor } from '@/frontend/reports/drawDocument';
import { Check, CheckResult, isRulesPath, readFixtureChecks, readRulesFile } from '@/reports/bundles/expectations';
import { judgeReport } from '@/reports/report';
import { fixtureInput } from '@/reports/bundles/fixtureInput';
import { CompiledReportBundle, FixtureResult, ReportBundleFiles } from '@/reports/bundles/types';
import { sampleRunReportContext } from '@/reports/bundles/fixture';
import { RunReportContext } from '@/reports/types';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { FillContextProvider } from '../../reportsV2/fillContext';
import { SheetForm } from '../../reportsV2/SheetForm';
import { Prop } from '../inputs/builderControls';
import { CheckEditor, CheckRow, ChecksUnread } from './CheckEditor';

export interface FixtureFile {
  inputs?: InputValues;
  values?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  traces?: Record<string, unknown>;
  /** a run report's: the run it reads */
  context?: Record<string, unknown>;
  expect?: { verdict?: string; verdictLabel?: string; checks?: unknown[] };
  [other: string]: unknown;
}

const VERDICTS = ['', 'passed', 'failed', 'incomplete'];
const NO_FILES = { editable: true, upload: async () => null, traces: {} };
const noUrls = async () => ({});

export const parseFixture = (text: string | undefined): FixtureFile | null => {
  try {
    const raw = JSON.parse(text ?? '') as unknown;
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as FixtureFile) : null;
  } catch {
    return null;
  }
};
export const writeFixture = (fixture: FixtureFile) => `${JSON.stringify(fixture, null, 2)}\n`;

/** A new fixture: for a report filled in from inputs, its schema filled in as given (or not at all); for a run report, the run given, or the sample. */
export function newFixture(sheet: InputSheet | null, verdict = 'passed', inputs?: InputValues, context?: RunReportContext): FixtureFile {
  return sheet ? { expect: { verdict }, inputs: inputs ?? emptyInputs(sheet), values: {} } : { expect: { verdict }, context: (context ?? sampleRunReportContext()) as unknown as Record<string, unknown> };
}

/** A finding as the report comes to it over a fixture. */
export interface FixtureFinding {
  id: string;
  message: string;
  passed: boolean;
}

/** The findings the draft, as last compiled, comes to over a fixture as it is typed; null while it cannot say. */
export function useFixtureFindings(compiled: CompiledReportBundle | null, path: string, text: string): FixtureFinding[] | null {
  return useMemo(() => {
    if (!compiled) return null;
    try {
      const module = moduleFor(compiled);
      const { definition } = module;
      const input = fixtureInput(compiled, path, text);
      const context = input.context as RunReportContext & { settings?: unknown };
      if (compiled.hasSettings && definition.normalizeSettings) context.settings = definition.normalizeSettings(input.settings ?? {});
      // the findings a check on one is held to: the verdict of record, as the server judges a fixture
      const model = judgeReport(definition, definition.normalizeValues(input.values ?? definition.defaultValues()), context);
      return model.findings.map((f) => ({ id: f.id, message: f.message, passed: f.passed }));
    } catch {
      return null;
    }
  }, [compiled?.hash, path, text]);
}

/** The result a check came to at the last save, matched to it as written. */
export const resultOf = (results: CheckResult[] | undefined, from: string, check: Check) => (results ?? []).find((r) => r.from === from && r.check && JSON.stringify(r.check) === JSON.stringify(check));

/**
 * A fixture file shown as what it is: what the report must come to with it,
 * whether it did at the last save, and for a report filled in from inputs, its
 * inputs on the schema's own form and the report's own values. The JSON beneath
 * is written as you go; the Code view is the same file.
 */
export function FixtureView({ path, text, sheet, result, compiled, files, onChange, onOpenCode, onPick, onOpenFile }: {
  path: string;
  text: string;
  /** the schema it fills in; null for a run report, whose fixtures carry a run */
  sheet: InputSheet | null;
  /** how it came out at the last save, the printed document's checks beside the compile's */
  result: (FixtureResult & { pending?: boolean }) | null;
  /** the draft as last compiled, to say what findings it comes to */
  compiled: CompiledReportBundle | null;
  /** the working files: the rules files that hold it too */
  files: ReportBundleFiles;
  onChange: (text: string) => void;
  onOpenCode: () => void;
  /** pick what it must print from the preview */
  onPick?: () => void;
  onOpenFile: (path: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const findings = useFixtureFindings(compiled, path, text);
  const fixture = parseFixture(text);
  const [valuesText, setValuesText] = useState(() => JSON.stringify(fixture?.values ?? {}, null, 2));
  const [valuesError, setValuesError] = useState(false);
  const inputs = useMemo(() => (sheet ? normalizeInputs(sheet, fixture?.inputs ?? {}) : {}), [sheet, fixture?.inputs]);
  const progress = useMemo(() => (sheet ? sheetProgress(sheet, inputs) : null), [sheet, inputs]);
  if (!fixture) {
    return (
      <div className="flex flex-col items-start gap-2 p-[1.5rem]">
        <p className="text-destructive">{I18n.get('rv2FixtureUnreadable', { file: path })}</p>
        <Button variant="outline" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>
      </div>
    );
  }
  const update = (change: Partial<FixtureFile>) => onChange(writeFixture({ ...fixture, ...change }));
  const expect = fixture.expect ?? {};
  const { checks: own, problems: unread } = readFixtureChecks(expect);
  // written over the checks as they are, not as read: one that could not be read stays for the author to fix in code
  const rawChecks: unknown[] = Array.isArray(expect.checks) ? expect.checks : [];
  const locked = unread.length > 0;
  const setChecks = (checks: unknown[]) => update({ expect: { ...expect, checks: checks.length ? checks : undefined } });
  const expectedFinding = (id: string) => own.find((c) => 'finding' in c && c.finding === id);
  // the rules files that hold it too: every one, or every one for the verdict it must come to
  const rules = Object.keys(files).filter(isRulesPath).sort().flatMap((rulesPath) => {
    const { rules: read, problems } = readRulesFile(files[rulesPath]);
    // one that cannot be read holds this fixture back too: it is shown, to be opened and fixed
    if (problems.length === 0 && (!read || read.checks.length === 0 || (read.when?.verdict && read.when.verdict !== expect.verdict))) return [];
    const made = (result?.checks ?? []).filter((r) => r.from === rulesPath);
    // what it could not read counts among its checks, missed, whatever the last save made of it; its checks only when they hold this fixture
    const applies = !!read && !(read.when?.verdict && read.when.verdict !== expect.verdict);
    return [{ path: rulesPath, count: (applies ? read.checks.length : 0) + problems.length, missed: Math.max(made.filter((r) => !r.ok).length, problems.length) }];
  });
  return (
    <div className="@container h-full overflow-y-auto bg-muted/30">
      <div className="mx-auto flex max-w-[52rem] flex-col gap-[1rem] px-[1.5rem] py-[1.25rem]">
        <section aria-label={I18n.get('designerFixtureMustComeTo')} className="flex flex-col gap-[0.75rem] rounded-xl border-[1px] bg-background p-[1rem]">
          <div className="grid gap-[0.75rem] @xl:grid-cols-2">
            <Prop label={I18n.get('rv2ExpectVerdict')}>
              <select aria-label={I18n.get('rv2ExpectVerdict')} value={expect.verdict ?? ''} onChange={(e) => update({ expect: { ...expect, verdict: e.target.value || undefined } })} className="h-8 rounded-lg border-[1px] border-input bg-background px-2 text-sm">
                {VERDICTS.map((v) => <option key={v} value={v}>{v ? I18n.get(`verdict_${v}`) : I18n.get('rv2NoExpectation')}</option>)}
              </select>
            </Prop>
            <Prop label={I18n.get('rv2ExpectLabel')} hint={I18n.get('rv2ExpectLabelHelp')}>
              <Input aria-label={I18n.get('rv2ExpectLabel')} value={expect.verdictLabel ?? ''} placeholder="PASS" onChange={(e) => update({ expect: { ...expect, verdictLabel: e.target.value || undefined } })} />
            </Prop>
          </div>
          {result && (
            <p className={cn('flex items-center gap-1.5 text-sm', result.ok ? 'text-emerald-700' : 'text-red-700')}>
              {result.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
              {result.ok ? I18n.get('rv2FixtureHolds', { label: result.verdictLabel ?? '' }) : I18n.get('rv2FixtureFails', { error: result.error ?? '' })}
            </p>
          )}
          {result && !result.ok && !!result.reasons?.length && (
            <div className="flex flex-col gap-1 text-sm text-red-700">
              <p>{I18n.get('rv2FixtureFailsBecause')}</p>
              <ul className="list-disc pl-[1.25rem] text-xs">{result.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
              <p className="text-xs">{I18n.get('rv2FixtureFailsHelp')}</p>
            </div>
          )}
        </section>

        <section aria-label={I18n.get('fixtureChecks')} className="flex flex-col rounded-xl border-[1px] bg-background">
          <div className="flex flex-wrap items-center gap-2 px-[1rem] pt-[0.75rem] pb-2">
            <h3 className="mr-auto text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{I18n.get('fixtureChecks')}</h3>
            {onPick && <Button variant="outline" size="xs" onClick={onPick} disabled={locked}><Crosshair /> {I18n.get('fixturePickFromPreview')}</Button>}
            <Button variant="ghost" size="xs" onClick={() => setAdding(true)} disabled={adding || locked}><Plus /> {I18n.get('fixtureAddCheck')}</Button>
          </div>
          {own.length === 0 && !adding && <p className="px-[1rem] pb-[0.75rem] text-sm text-muted-foreground">{I18n.get('fixtureNoChecks')}</p>}
          {own.length > 0 && (
            <ul aria-label={I18n.get('fixtureChecks')} className="divide-y border-t-[1px]">
              {own.map((check, i) => {
                const made = resultOf(result?.checks, path, check);
                return <CheckRow key={i} check={check} result={made} pending={!made && !!result?.pending && 'printed' in check} onRemove={locked ? undefined : () => setChecks(rawChecks.filter((_, j) => j !== i))} />;
              })}
            </ul>
          )}
          {adding && (
            <div className="border-t-[1px] p-[0.75rem]">
              <CheckEditor findings={findings} onCancel={() => setAdding(false)} onAdd={(check) => { setChecks([...rawChecks, check]); setAdding(false); }} />
            </div>
          )}
          {locked && <ChecksUnread problems={unread} onOpenCode={onOpenCode} />}
          {rules.length > 0 && (
            <div className="flex flex-col gap-1 border-t-[1px] px-[1rem] py-2 text-xs text-muted-foreground">
              {rules.map((r) => (
                <button key={r.path} type="button" onClick={() => onOpenFile(r.path)} className="inline-flex items-center gap-1.5 self-start hover:text-foreground hover:underline">
                  {r.missed ? <XCircle className="size-3.5 text-red-600" /> : <CheckCircle2 className="size-3.5 text-emerald-600" />}
                  {I18n.get(r.missed ? 'fixtureRulesMissed' : r.count === 1 ? 'fixtureRulesHoldOne' : 'fixtureRulesHold', { count: String(r.count), file: r.path, missed: String(r.missed) })}
                  <ExternalLink className="size-3" />
                </button>
              ))}
            </div>
          )}
        </section>

        {findings && findings.length > 0 && (
          <section aria-label={I18n.get('fixtureFindings')} className="flex flex-col rounded-xl border-[1px] bg-background">
            <div className="px-[1rem] pt-[0.75rem] pb-2">
              <h3 className="text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{I18n.get('fixtureFindings')}</h3>
              <p className="text-xs text-muted-foreground">{I18n.get('fixtureFindingsHelp')}</p>
            </div>
            <ul aria-label={I18n.get('fixtureFindings')} className="divide-y border-t-[1px]">
              {findings.map((f) => {
                const expected = expectedFinding(f.id);
                return (
                  <li key={f.id} className="flex items-center gap-2 px-[1rem] py-1.5 text-sm">
                    <span className={cn('w-12 shrink-0 rounded-sm px-1 text-center text-[0.6rem] font-semibold', f.passed ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900')}>{I18n.get(f.passed ? 'expectPasses' : 'expectFails')}</span>
                    <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{f.message} <span className="font-mono text-xs text-muted-foreground">{f.id}</span></span>
                    {expected
                      ? <span className="shrink-0 text-xs text-muted-foreground">{I18n.get('fixtureFindingExpected')}</span>
                      : <Button variant="outline" size="xs" disabled={locked} onClick={() => setChecks([...rawChecks, { finding: f.id, passes: f.passed }])}>{I18n.get('fixtureExpectFinding')}</Button>}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {sheet && progress ? (
          <>
            <section aria-label={I18n.get('designerFixtureValues')} className="flex flex-col gap-2 rounded-xl border-[1px] bg-background p-[1rem]">
              <Prop label={I18n.get('rv2FixtureValues')} hint={valuesError ? I18n.get('rv2NotJson') : I18n.get('rv2FixtureValuesHelp')}>
                <Textarea aria-label={I18n.get('rv2FixtureValues')} className="font-mono text-xs" value={valuesText} aria-invalid={valuesError || undefined} onChange={(e) => {
                  setValuesText(e.target.value);
                  try {
                    update({ values: JSON.parse(e.target.value) as Record<string, unknown> });
                    setValuesError(false);
                  } catch {
                    setValuesError(true);
                  }
                }} />
              </Prop>
            </section>
            <h3 className="text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">
              {I18n.get('designerFixtureInputs')} <span className="font-normal normal-case">· {I18n.get('rv2InputsFilled', { filled: String(progress.filled), total: String(progress.total) })} · {I18n.get('rv2FixtureFilesNote')}</span>
            </h3>
            <FillContextProvider value={NO_FILES}>
              <ReportAssetsProvider resolve={noUrls}>
                <SheetForm sheet={sheet} inputs={inputs} progress={progress.sections} onChange={(id, value) => update({ inputs: { ...(fixture.inputs ?? {}), [id]: value } })} />
              </ReportAssetsProvider>
            </FillContextProvider>
          </>
        ) : (
          <div className="flex flex-col items-start gap-2 rounded-xl border-[1px] bg-background p-[1rem]">
            <p className="text-muted-foreground">{I18n.get('rv2RunFixturesAsCode')}</p>
            <Button variant="outline" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
